import type { Components } from '@/api/generated/api';
import type { ISelectedCompetence, TStudentLike } from '@/interfaces';
import { toCompetenceSelectionRecord } from '@/utils/competence.domain.utils';
import { isWholeClassStudentSelection, resolveStudentSelection } from '@/utils/students.utils';
import { uniquePrimitives } from 'common/utils/array.utils';
import { now } from 'common/utils/date.utils';
import { computed, ref, toValue, type MaybeRefOrGetter, type Ref } from 'vue';
import {
  getSupportMeasureProtocolValidationIssues,
  type ISupportMeasureProtocolValidationIssue,
} from './supportMeasureProtocol.validation';

export type TSupportMeasureProtocolSelectedMaterial = {
  externalMaterialId: number;
  item: Components.Schemas.RecommendedExternalMaterialDto | Components.Schemas.SupportMeasureEntryMaterialDto;
};

interface IUseSupportMeasureProtocolDraftParams {
  allCompetenceDomains: Readonly<Ref<Components.Schemas.CompetenceDomainDto[]>>;
  connectedCompetenceIds: MaybeRefOrGetter<readonly number[]>;
  currentCompetenceAssessment: Readonly<Ref<Components.Schemas.CompetenceAssessmentDto | null>>;
  editProtocolEntry: MaybeRefOrGetter<Components.Schemas.SupportMeasureProtocolEntryDto | null | undefined>;
  /** Selectable students that are no longer members of the measure: their existing ratings are kept as they are. */
  historicalStudentIds: MaybeRefOrGetter<readonly number[]>;
  schoolSubjectId: MaybeRefOrGetter<number>;
  selectedStudentId: MaybeRefOrGetter<number | null>;
  students: MaybeRefOrGetter<readonly Components.Schemas.BaseStudentDto[]>;
}

type TManualStudentSelection = { all: boolean; ids: number[] };

export const useSupportMeasureProtocolDraft = (params: IUseSupportMeasureProtocolDraftParams) => {
  const text = ref('');
  const date = ref<string>(now().toISOString());
  const selectedCompetenceIds = ref<number[]>([]);
  const levelByCompetenceId = ref<Record<number, number | null>>({});
  const originalAssessmentIdByCompetenceId = ref<Map<number, number>>(new Map());
  const selectedMaterials = ref<TSupportMeasureProtocolSelectedMaterial[]>([]);
  const manualStudentSelection = ref<TManualStudentSelection | null>(null);

  const students = computed(() => toValue(params.students));
  const preselectedMemberId = computed<number | null>(() => {
    const selectedStudentId = toValue(params.selectedStudentId);
    return selectedStudentId != null && students.value.some((student) => student.id === selectedStudentId)
      ? selectedStudentId
      : null;
  });
  const allStudentsSelected = computed<boolean>(() =>
    manualStudentSelection.value ? manualStudentSelection.value.all : preselectedMemberId.value == null,
  );
  const explicitStudentIds = computed<number[]>(() => {
    if (manualStudentSelection.value) return manualStudentSelection.value.ids;
    return preselectedMemberId.value == null ? [] : [preselectedMemberId.value];
  });
  const explicitSelectedStudents = computed<TStudentLike[]>(() => {
    const selectedIds = new Set(explicitStudentIds.value);
    return students.value.filter((student) => selectedIds.has(student.id));
  });
  const selectedStudents = computed<TStudentLike[]>(() =>
    resolveStudentSelection({
      allEligibleStudentsSelected: allStudentsSelected.value,
      eligibleStudents: [...students.value],
      selectedStudents: explicitSelectedStudents.value,
    }),
  );
  const resolvedTargetStudentIds = computed<number[]>(() => selectedStudents.value.map((student) => student.id));

  const subjectDomains = computed<Components.Schemas.CompetenceDomainDto[]>(() =>
    params.allCompetenceDomains.value.filter((domain) => domain.schoolSubjectId === toValue(params.schoolSubjectId)),
  );
  const connectedCompetenceIdSet = computed<Set<number>>(() => new Set(toValue(params.connectedCompetenceIds)));
  const competenceRowById = computed(() => toCompetenceSelectionRecord(subjectDomains.value));
  const selectedCompetenceRows = computed<ISelectedCompetence[]>(() =>
    selectedCompetenceIds.value
      .map((id) => competenceRowById.value[id])
      .filter((row): row is ISelectedCompetence => row != null),
  );
  const selectedCompetences = computed<Components.Schemas.CompetenceDto[]>(() =>
    selectedCompetenceRows.value.map((row) => row.competence),
  );
  const competenceAssessment = computed(() => params.currentCompetenceAssessment.value ?? undefined);
  const canRateCompetences = computed<boolean>(() => competenceAssessment.value != null);
  const validationIssues = computed(() =>
    getSupportMeasureProtocolValidationIssues({
      competenceIds: selectedCompetenceIds.value,
      levelByCompetenceId: levelByCompetenceId.value,
      resolvedStudentCount: resolvedTargetStudentIds.value.length,
      text: text.value,
    }),
  );
  const nextValidationIssue = computed<ISupportMeasureProtocolValidationIssue | null>(
    () => validationIssues.value[0] ?? null,
  );
  const canSubmit = computed<boolean>(() => validationIssues.value.length === 0);
  const isPristine = computed<boolean>(
    () => text.value.trim().length === 0 && !selectedCompetenceIds.value.length && !selectedMaterials.value.length,
  );
  const newlyConnectedCompetenceIds = computed<number[]>(() =>
    selectedCompetenceIds.value.filter((id) => !connectedCompetenceIdSet.value.has(id)),
  );
  const historicalStudentIdSet = computed<Set<number>>(() => new Set(toValue(params.historicalStudentIds)));
  // observations of the edited protocol entry that belong to participants who already left the measure
  const historicalObservations = computed<Components.Schemas.BaseCompetenceObservationDto[]>(() => {
    const entry = toValue(params.editProtocolEntry);
    if (!entry) return [];
    return entry.competenceObservations.filter((observation) =>
      historicalStudentIdSet.value.has(observation.studentId),
    );
  });

  const setStudents = (updatedStudents: readonly TStudentLike[]): void => {
    const validStudentIds = new Set(students.value.map((student) => student.id));
    manualStudentSelection.value = {
      all: isWholeClassStudentSelection([...updatedStudents], [...students.value]),
      ids: uniquePrimitives(updatedStudents.map((student) => student.id)).filter((id) => validStudentIds.has(id)),
    };
  };
  const selectAllStudents = (): void => {
    manualStudentSelection.value = { all: true, ids: [] };
  };
  const addStudent = (studentId: number): void => {
    const student = students.value.find((candidate) => candidate.id === studentId);
    if (!student) return;
    if (allStudentsSelected.value) {
      setStudents([student]);
      return;
    }
    if (selectedStudents.value.some((selectedStudent) => selectedStudent.id === studentId)) return;
    setStudents([...selectedStudents.value, student]);
  };
  const addCompetence = (competenceId: number): void => {
    if (selectedCompetenceIds.value.includes(competenceId)) return;
    selectedCompetenceIds.value = [...selectedCompetenceIds.value, competenceId];
    levelByCompetenceId.value = { ...levelByCompetenceId.value, [competenceId]: null };
  };
  const removeCompetence = (competenceId: number): void => {
    selectedCompetenceIds.value = selectedCompetenceIds.value.filter((id) => id !== competenceId);
    const nextLevels = { ...levelByCompetenceId.value };
    delete nextLevels[competenceId];
    levelByCompetenceId.value = nextLevels;
  };
  const setCompetenceLevel = (competenceId: number, value: number | null): void => {
    levelByCompetenceId.value = { ...levelByCompetenceId.value, [competenceId]: value };
  };
  const addMaterial = (material: Components.Schemas.RecommendedExternalMaterialDto): void => {
    if (selectedMaterials.value.some((selected) => selected.externalMaterialId === material.id)) return;
    selectedMaterials.value = [...selectedMaterials.value, { externalMaterialId: material.id, item: material }];
  };
  const removeMaterial = (externalMaterialId: number): void => {
    selectedMaterials.value = selectedMaterials.value.filter(
      (material) => material.externalMaterialId !== externalMaterialId,
    );
  };

  const buildObservations = (): Components.Schemas.ProtocolCompetenceObservationInputDto[] => {
    const currentAssessmentId = competenceAssessment.value?.id;
    const observations: Components.Schemas.ProtocolCompetenceObservationInputDto[] = [];
    for (const studentId of resolvedTargetStudentIds.value) {
      // students that left the measure keep their own ratings — see below
      if (historicalStudentIdSet.value.has(studentId)) continue;
      for (const competenceId of selectedCompetenceIds.value) {
        const performanceLevelV2Id = levelByCompetenceId.value[competenceId];
        if (performanceLevelV2Id == null) continue;
        const competenceAssessmentId =
          originalAssessmentIdByCompetenceId.value.get(competenceId) ?? currentAssessmentId;
        if (competenceAssessmentId == null) continue;
        observations.push({ studentId, competenceId, competenceAssessmentId, performanceLevelV2Id });
      }
    }
    // The update replaces every observation of the protocol entry, so the ratings of still-selected participants
    // that left the measure are re-sent unchanged — the rating UI holds one level per competence and cannot
    // express theirs. Deselecting such a student, or dropping a competence, therefore removes their rows.
    const targetStudentIds = new Set(resolvedTargetStudentIds.value);
    for (const observation of historicalObservations.value) {
      if (observation.performanceLevelV2Id == null) continue;
      if (!targetStudentIds.has(observation.studentId)) continue;
      if (!selectedCompetenceIds.value.includes(observation.competenceId)) continue;
      observations.push({
        studentId: observation.studentId,
        competenceId: observation.competenceId,
        competenceAssessmentId: observation.competenceAssessmentId,
        performanceLevelV2Id: observation.performanceLevelV2Id,
        description: observation.description,
      });
    }
    return observations;
  };

  const resetAfterCreate = (): void => {
    text.value = '';
    date.value = now().toISOString();
    selectedCompetenceIds.value = [];
    levelByCompetenceId.value = {};
    originalAssessmentIdByCompetenceId.value = new Map();
    selectedMaterials.value = [];
  };

  const initializeFromEntry = (entry: Components.Schemas.SupportMeasureProtocolEntryDto): void => {
    text.value = entry.text;
    date.value = entry.date;
    selectedMaterials.value = entry.materials.map((material) => ({
      externalMaterialId: material.externalMaterialId,
      item: material,
    }));
    setStudents(entry.students);
    const levels = new Map<number, number | null>();
    const assessments = new Map<number, number>();
    for (const observation of entry.competenceObservations) {
      if (!levels.has(observation.competenceId)) {
        levels.set(observation.competenceId, observation.performanceLevelV2Id);
      }
      if (!assessments.has(observation.competenceId)) {
        assessments.set(observation.competenceId, observation.competenceAssessmentId);
      }
    }
    selectedCompetenceIds.value = [...levels.keys()];
    levelByCompetenceId.value = Object.fromEntries(levels);
    originalAssessmentIdByCompetenceId.value = assessments;
  };

  const editProtocolEntry = toValue(params.editProtocolEntry);
  if (editProtocolEntry) initializeFromEntry(editProtocolEntry);

  return {
    addCompetence,
    addMaterial,
    addStudent,
    allStudentsSelected,
    buildObservations,
    canSubmit,
    canRateCompetences,
    competenceAssessment,
    date,
    isPristine,
    levelByCompetenceId,
    newlyConnectedCompetenceIds,
    nextValidationIssue,
    removeCompetence,
    removeMaterial,
    resetAfterCreate,
    resolvedTargetStudentIds,
    selectAllStudents,
    selectedCompetenceIds,
    selectedCompetenceRows,
    selectedCompetences,
    selectedMaterials,
    selectedStudents,
    setCompetenceLevel,
    setStudents,
    subjectDomains,
    text,
  };
};
